import { DateStrategyAssign } from "app/shared/model/EditPricelist";
import { toLocalDateString } from "../date/iso";
import { normalizeRecurranceStartDays } from "./recurranceDays";

export type NormalizeStrategyOptions = {
  ensureFlags?: boolean;
};

const normRequired = (d: any): string => toLocalDateString(d) || "";
const normOptional = (d: any) => toLocalDateString(d); // string | null | undefined

const ensureFlags = <T extends { isDeleted?: boolean; isNew?: boolean }>(x: T) => ({
  ...x,
  isDeleted: Boolean(x.isDeleted),
  isNew: Boolean(x.isNew),
});

export const normalizeStrategy = (
    ds: DateStrategyAssign,
    opts: NormalizeStrategyOptions = {}
): DateStrategyAssign => {
  const withFlags = opts.ensureFlags ?? true;

  const fromDate = normRequired(ds.fromDate);
  const originalFromDate = normRequired((ds as any).originalFromDate) || fromDate;

  const specificStartDates = (ds.specificStartDates ?? []).map((x) => {
    const base = {
      ...x,
      date: normRequired((x as any).date),
    };
    return withFlags ? ensureFlags(base) : base;
  });

  const fixedDurationDates = (ds.fixedDurationDates ?? []).map((x) => {
    const base = {
      ...x,
      startDate: normRequired((x as any).startDate),
      endDate: normRequired((x as any).endDate),
    };
    return withFlags ? ensureFlags(base) : base;
  });

  return {
    ...ds,
    fromDate,
    toDate: normOptional(ds.toDate),
    lastStartDate: normOptional(ds.lastStartDate),

    originalFromDate,
    originalToDate: normOptional((ds as any).originalToDate),

    specificStartDates,
    fixedDurationDates,
  };
};

export const sanitizeDateStrategy = (ds: any): DateStrategyAssign => {
  const from = toLocalDateString(ds?.fromDate ?? ds?.FromDate);
  const to = ds?.toDate ?? ds?.ToDate;
  const last = ds?.lastStartDate ?? ds?.LastStartDate;
  const origFrom = toLocalDateString(ds?.originalFromDate ?? ds?.OriginalFromDate) ?? from;

  return {
    strategyTypeId: Number(ds?.strategyTypeId ?? ds?.StrategyTypeId ?? 0),
    fromDate: from || '',
    toDate: toLocalDateString(to),
    lastStartDate: toLocalDateString(last),
    recurranceStartDays: normalizeRecurranceStartDays(
      ds?.recurranceStartDays ?? ds?.RecurranceStartDays
    ),
    specificStartDates: (
        ds?.specificStartDates ??
        ds?.SpecificStartDates ??
        []
    ).map((x: any) => ({
      date: toLocalDateString(x?.date ?? x?.Date) || '',
      isNew: Boolean(x?.isNew ?? x?.IsNew),
      isDeleted: Boolean(x?.isDeleted ?? x?.IsDeleted),
    })),
    fixedDurationDates: (
        ds?.fixedDurationDates ??
        ds?.FixedDurationDates ??
        []
    ).map((x: any) => ({
      startDate: toLocalDateString(x?.startDate ?? x?.StartDate) || '',
      endDate: toLocalDateString(x?.endDate ?? x?.EndDate) || '',
      isNew: Boolean(x?.isNew ?? x?.IsNew),
      isDeleted: Boolean(x?.isDeleted ?? x?.IsDeleted),
    })),
    originalFromDate: origFrom || '',
    originalToDate: toLocalDateString(ds?.originalToDate ?? ds?.OriginalToDate),
    isArchived: Boolean(ds?.isArchived ?? ds?.IsArchived),
    isNew: Boolean(ds?.isNew ?? ds?.IsNew),
  };
};

export const normalizeNewOfferingStrategy = (ds: DateStrategyAssign): DateStrategyAssign => {
  const norm = (d: any) => toLocalDateString(d) || "";

  return {
    ...ds,
    isNew: true,
    fromDate: norm(ds.fromDate) as any,
    toDate: ds.toDate ? (norm(ds.toDate) as any) : undefined,
    lastStartDate: ds.lastStartDate ? (norm(ds.lastStartDate) as any) : undefined,

    originalFromDate: (norm((ds as any).originalFromDate) || norm(ds.fromDate)) as any,
    originalToDate: (ds as any).originalToDate
        ? (norm((ds as any).originalToDate) as any)
        : undefined,

    specificStartDates: (ds.specificStartDates ?? [])
        .filter((x: any) => !x?.isDeleted)
        .map((x: any) => ({ ...x, date: norm(x?.date) as any })),

    fixedDurationDates: (ds.fixedDurationDates ?? [])
        .filter((x: any) => !x?.isDeleted)
        .map((x: any) => ({
          ...x,
          startDate: norm(x?.startDate) as any,
          endDate: norm(x?.endDate) as any,
        })),
  };
};